import { useIntl } from "react-intl";
import { TripSearchResult } from "src/domain/TripPlanner/Drawer/TripSearchResults/TripSearchResult";
import { getSearchTransportKey } from "src/domain/TripPlanner/util/getSearchTransportKey";
import { useTripPlannerContext } from "src/domain/TripPlanner/hooks/useTripPlannerContext";
import { getCardRouteIndex } from "src/domain/TripPlanner/TripPlannerCard/createTripCardProperties";
import { RadioGroup } from "src/components/RadioGroup/RadioGroup";
import {
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  Fragment,
  useState,
} from "react";
import { ShowMoreButton } from "src/components/TripPlanner/TripCard/ShowMoreButton";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import { InlineSerpAd } from "src/domain/InlineAds/InlineSerpAd/InlineSerpAd";
import { useScreenMinWidth } from "src/utils/hooks/useScreenMinWidth";
import type { SearchResponse } from "../../../api/SearchResponse";
import { createSearchResultViewModel } from "../createSearchResultViewModel";
import {
  originPlaceFromSearch,
  destinationPlaceFromSearch,
} from "../../../utils/adapters/place";
import { SearchResult } from "../SearchResult/SearchResult";
import messages from "./SearchResultsContent.messages";

type Props = {
  context: "transport" | "tripPlanner";
  searchResponse: SearchResponse;
  handleTripSave: (routeIndex?: string) => void;
};

const ConditionalSearchResultWrapper = ({
  children,
  wrapper,
}: PropsWithChildren<{ wrapper: (children: ReactNode) => ReactElement }>) => {
  return wrapper(children);
};

export function SearchResultsContent(props: Props) {
  const intl = useIntl();
  const searchResults = createSearchResultViewModel(props.searchResponse);
  const originPlace = originPlaceFromSearch(props.searchResponse);
  const destinationPlace = destinationPlaceFromSearch(props.searchResponse);
  const { isMobile } = useScreenMinWidth();
  const initialNumResults =
    isMobile || searchResults.length <= 5 ? searchResults.length : 4;
  const [numResult, setNumResults] = useState(initialNumResults);

  const isSingular = searchResults.length === 1;
  const headingMesage = isSingular
    ? messages.headingSingleRoute
    : messages.headingMultipleRoutes;

  const formattedHeading = intl.formatMessage(headingMesage, {
    numberOfRoutes: searchResults.length,
    origin: originPlace.shortName,
    destination: destinationPlace.shortName,
  });

  const SearchResultListing =
    props.context === "tripPlanner" || !isMobile
      ? TripSearchResult
      : SearchResult;

  const { tripPlannerDetails } = useTripPlannerContext();
  const transportKey = getSearchTransportKey(props.searchResponse);
  const transport = tripPlannerDetails.transport[transportKey];
  const selectedRouteIndex = transport?.selectedRouteIndex;
  const defaultSelectedRouteIndex =
    tripPlannerDetails.places.length > 2 ? "0" : "";
  const routeIndex = transport
    ? getCardRouteIndex({
        ...transport,
        searchResponse: props.searchResponse,
      })
    : undefined;

  function handleShowMoreClick() {
    setNumResults(searchResults.length);
    sendAnalyticsInteractionEvent({
      category: "SearchResults",
      action: "Click:ShowMoreResults",
    });
  }

  function handleShowLessClick() {
    setNumResults(initialNumResults);
    sendAnalyticsInteractionEvent({
      category: "SearchResults",
      action: "Click:ShowLessResults",
    });
  }

  const dynamicSearchResultsList = !isMobile
    ? searchResults.slice(0, numResult)
    : searchResults;

  function renderInlineAd(listingIndex: number) {
    return listingIndex === 0 && props.context !== "tripPlanner";
  }

  return (
    <>
      {props.context === "tripPlanner" ? (
        <div className="relative md:bg-transparent">
          <h1 className="mx-3 my-3 mt-3 mb-0 text-heading-lg font-medium text-neutral-1600 lg:m-0 lg:px-5 lg:py-2 lg:pb-5 lg:text-2xl lg:font-semibold">
            {formattedHeading}
          </h1>
        </div>
      ) : (
        <>
          <div className="mb-4">
            <h2 className="text-heading-lg" data-testid="serp-heading">
              {formattedHeading}
            </h2>
          </div>
        </>
      )}

      <ConditionalSearchResultWrapper
        wrapper={
          props.context === "tripPlanner"
            ? (children) => (
                <RadioGroup
                  name="search-result"
                  value={routeIndex?.toString() ?? defaultSelectedRouteIndex}
                  onChange={props.handleTripSave}
                  className="px-2 py-0.5 md:p-0"
                >
                  {children}
                </RadioGroup>
              )
            : (children) => <>{children}</>
        }
      >
        {dynamicSearchResultsList.map((searchResult, listingIndex) => (
          <Fragment key={searchResult.title}>
            {renderInlineAd(listingIndex) && isMobile && <InlineSerpAd />}
            <SearchResultListing
              $size="sm"
              selectedRouteIndex={selectedRouteIndex?.toString()}
              isRadioHidden={props.context === "transport"}
              isSearchScreen={props.context === "transport"}
              {...searchResult}
            />
            {renderInlineAd(listingIndex) && !isMobile && <InlineSerpAd />}
          </Fragment>
        ))}
        {initialNumResults < searchResults.length && (
          <ShowMoreButton
            onOpenClick={handleShowMoreClick}
            onCloseClick={handleShowLessClick}
            numResults={numResult}
            searchResultsLength={searchResults.length}
          />
        )}
      </ConditionalSearchResultWrapper>
    </>
  );
}
